<template>
  <div id="AdminDashboard" class="container">
    <div class="row justify-content-between align-items-center mb-3 mt-4">
      <div class="col-auto">
        <h1>Operations Dashboard</h1>
      </div>
    </div>

    <!-- Most Important Links Section -->
    <div class="card mb-4">
      <div class="card-body">
        <h5 class="card-title mb-3">Most Important Links</h5>
        <div class="row g-3">
          <div class="col-sm-6 col-md-4">
            <router-link
              :to="{ name: 'AdminAccountsView' }"
              class="btn btn-outline-primary w-100"
            >
              Manage accounts
            </router-link>
          </div>
          <div class="col-sm-6 col-md-4">
            <router-link
              :to="{ name: 'AdminTransactionsListView' }"
              class="btn btn-outline-primary w-100"
            >
              Manage spot inventory
            </router-link>
          </div>
          <div class="col-sm-6 col-md-4">
            <router-link
              :to="{ name: 'ForwardsOverview' }"
              class="btn btn-outline-primary w-100"
            >
              Manage forward deals
            </router-link>
          </div>
          <div class="col-sm-6 col-md-4">
            <router-link
              :to="{ name: 'AdminEacPricingView' }"
              class="btn btn-outline-primary w-100"
            >
              Review market performance
            </router-link>
          </div>
          <div class="col-sm-6 col-md-4">
            <router-link
              :to="{ name: 'AdminCompanyOrdersView' }"
              class="btn btn-outline-primary w-100"
            >
              Review company orders
            </router-link>
          </div>
          <div class="col-sm-6 col-md-4">
            <router-link
              :to="{ name: 'AdminTransactionsPerformanceView' }"
              class="btn btn-outline-primary w-100"
            >
              Review Soldera Performance
            </router-link>
          </div>
          <div class="col-sm-6 col-md-4">
            <router-link
              :to="{ name: 'AdminBillingView' }"
              class="btn btn-outline-success w-100"
            >
              Billing
            </router-link>
          </div>
          <div class="col-sm-6 col-md-4">
            <router-link
              :to="{ name: 'AdminFinancialDashboardView' }"
              class="btn btn-outline-success w-100"
            >
              Finance Dashboard
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner">
        <LoadingIndicatorComponent message="Loading dashboard data..." />
      </div>
    </div>

    <!-- Section 1: Filtered Data (respects filters) -->
    <div class="mb-5" :class="{ 'content-loading': isLoading }">
      <h2 class="mb-3">Filtered Data</h2>

      <div class="row justify-content-between align-items-center mb-3">
        <div class="col-auto"></div>
        <div class="col d-flex gap-3 justify-content-end align-items-center">
          <div class="dropdown">
            <button
              class="btn btn-outline-secondary dropdown-toggle"
              type="button"
              id="portfolioDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              Portfolio Types ({{ filters.portfolio_types.length }} selected)
            </button>
            <ul
              class="dropdown-menu portfolio-dropdown"
              aria-labelledby="portfolioDropdown"
            >
              <li
                v-for="type in portfolioTypeOptions"
                :key="type.portfolio_type"
              >
                <div class="dropdown-item">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="`type-${type.portfolio_type}`"
                      :value="type.portfolio_type"
                      v-model="filters.portfolio_types"
                    />
                    <label
                      class="form-check-label"
                      :for="`type-${type.portfolio_type}`"
                    >
                      {{ type.name }}
                    </label>
                  </div>
                </div>
              </li>
            </ul>
          </div>

          <div class="dropdown">
            <button
              class="btn btn-outline-secondary dropdown-toggle"
              type="button"
              id="technologyDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              Technology Types ({{ filters.technology_prefixes.length }}
              selected)
            </button>
            <ul
              class="dropdown-menu portfolio-dropdown"
              aria-labelledby="technologyDropdown"
            >
              <li v-for="type in TECHNOLOGY_TYPES" :key="type.tech_code_prefix">
                <div class="dropdown-item">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="`tech-${type.tech_code_prefix}`"
                      :value="type.tech_code_prefix"
                      v-model="filters.technology_prefixes"
                    />
                    <label
                      class="form-check-label"
                      :for="`tech-${type.tech_code_prefix}`"
                    >
                      {{ type.name }}
                    </label>
                  </div>
                </div>
              </li>
            </ul>
          </div>

          <div class="input-group" style="width: auto">
            <span class="input-group-text">Expected Price €</span>
            <input
              type="number"
              class="form-control"
              v-model.number="filters.expected_price"
              placeholder="No limit"
              style="width: 100px"
            />
          </div>

          <div style="width: 200px">
            <MultiSelectComponent
              v-model="filters.country_codes"
              :options="SUPPORTED_COUNTRIES_WITH_LABELS"
              placeholder="All countries"
              size="sm"
            />
          </div>

          <div style="width: 200px">
            <CompanyMultiSelectComponent
              v-model="filters.company_ids"
              placeholder="Select companies"
              size="sm"
            />
          </div>

          <button
            class="btn btn-primary"
            @click="onFilterChange"
            :disabled="isLoading"
          >
            <span
              v-if="isLoading"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            {{ isLoading ? "Loading..." : "Filter" }}
          </button>
        </div>
      </div>

      <div class="row g-3">
        <!-- Estimated Production by Month Chart -->
        <div class="col-12 col-xl-8">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">Estimated Production by Month</h5>
              <p class="text-muted small mb-2">
                Filters: Countries, Companies | Annual production:
                {{
                  (statistics.projected_power_totals.total / 1000).toFixed(2)
                }}
                GWh/year
              </p>
              <canvas
                ref="projectionChartRef"
                style="max-height: 300px"
              ></canvas>
            </div>
          </div>
        </div>

        <div class="col-12 col-xl-4">
          <div class="row g-3">
            <!-- High level stats -->
            <div class="col-12">
              <div class="card h-100">
                <div class="card-body">
                  <h5 class="card-title">High Level Stats</h5>
                  <p class="text-muted small mb-2">
                    Filters: Countries, Companies
                  </p>
                  <p class="card-text mb-1">
                    <strong>Total Accounts:</strong>
                    {{ statistics.account_count }}
                  </p>
                  <p class="card-text mb-1">
                    Total Companies: {{ statistics.company_count }}
                  </p>
                  <p class="card-text mb-1">
                    <strong>Companies with Devices:</strong>
                    {{ statistics.companies_with_registry_devices_count }}
                  </p>
                  <p class="card-text mb-1">
                    <strong>Companies with Signed Agreement:</strong>
                    {{ statistics.companies_with_signed_agreement }}
                  </p>
                  <p class="card-text mb-1">
                    With Certificates:
                    {{ statistics.companies_with_certificates_count }}
                  </p>
                  <p class="card-text mb-0">
                    <strong>Total Registry Devices:</strong>
                    {{ statistics.total_registry_devices }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-xl-6">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">
                GOs in filtered portfolios (Last 12 Months)
              </h5>
              <p class="text-muted small mb-2">
                Filters: Portfolio Types, Technology Types, Expected Price,
                Countries, Companies
              </p>
              <p class="text-muted small mb-2">
                Total: {{ totalGOs.toLocaleString() }} | Supported:
                {{ supportedGOs.toLocaleString() }} | Not Supported:
                {{ unsupportedGOs.toLocaleString() }}
              </p>
              <canvas ref="chartRef" style="max-height: 300px"></canvas>
            </div>
          </div>
        </div>

        <!-- GO Distribution by Country -->
        <div class="col-12 col-xl-6">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">GO Distribution by Country</h5>
              <p class="text-muted small mb-2">
                Filters: Portfolio Types, Technology Types, Expected Price,
                Countries, Companies
              </p>
              <div class="d-flex flex-column gap-2">
                <canvas
                  ref="countryChartRef"
                  style="max-height: 250px"
                ></canvas>
                <div
                  v-if="countryLegendItems.length > 0"
                  class="d-flex flex-column gap-2 p-2 bg-light rounded"
                >
                  <div
                    v-for="legend in countryLegendItems"
                    :key="`legend-${legend.label}`"
                    class="d-flex align-items-center gap-2"
                  >
                    <span
                      class="d-inline-block rounded"
                      :style="{
                        backgroundColor: legend.color,
                        width: '16px',
                        height: '16px',
                      }"
                    ></span>
                    <span class="small">
                      <strong>{{ legend.label }}</strong> -
                      {{ legend.value.toLocaleString() }} ({{
                        legend.percentage
                      }}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Production Allocation Strategy -->
        <div class="col-12">
          <AdminAllocationChart :filters="filters" />
        </div>

        <!-- Predicted production by country and technology (GWh) -->
        <div class="col-12">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">
                Predicted production by country and technology (GWh)
              </h5>
              <p class="text-muted small mb-2">Filters: Countries, Companies</p>
              <div class="row">
                <div class="col-md-4 border-end">
                  <div class="mb-3">
                    <p class="card-text mb-1">
                      <strong>Total:</strong>
                      {{
                        (
                          statistics.projected_power_totals.total / 1000
                        ).toFixed(2)
                      }}
                      GWh
                    </p>
                    <div class="ms-3 small text-muted">
                      <div v-for="tech in ENERGY_TECHNOLOGIES" :key="tech">
                        - {{ TECHNOLOGIES[tech].name }}:
                        {{
                          (
                            statistics.projected_power_totals[tech] / 1000
                          ).toFixed(2)
                        }}
                        GWh
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-8">
                  <div class="row g-3">
                    <div
                      v-for="(
                        power, country
                      ) in statistics.power_summary_by_country"
                      :key="country"
                      class="col-sm-6 col-lg-4 mb-2"
                    >
                      <h6 class="mb-1 border-bottom pb-1">{{ country }}</h6>
                      <p class="card-text mb-1 small">
                        <strong>Total:</strong>
                        {{
                          (
                            sumTechnologyTotals(
                              power.projected_power_by_technology
                            ) / 1000
                          ).toFixed(2)
                        }}
                        GWh
                      </p>
                      <div
                        class="ms-2 small text-muted"
                        style="font-size: 0.75rem"
                      >
                        <template
                          v-for="tech in ENERGY_TECHNOLOGIES"
                          :key="tech"
                        >
                          <div
                            v-if="power.projected_power_by_technology[tech] > 0"
                          >
                            {{ TECHNOLOGIES[tech].name }}:
                            {{
                              (
                                power.projected_power_by_technology[tech] / 1000
                              ).toFixed(2)
                            }}
                            GWh
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Power by country and technology (MW) -->
        <div class="col-12">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">Power by country and technology (MW)</h5>
              <p class="text-muted small mb-2">Filters: Countries, Companies</p>
              <div class="row">
                <div class="col-md-4 border-end">
                  <div class="mb-3">
                    <p class="card-text mb-1">
                      <strong>Total:</strong>
                      {{
                        (statistics.total_registry_device_power / 1000).toFixed(
                          2
                        )
                      }}
                      MW
                    </p>
                    <div class="ms-3 small text-muted">
                      <div v-for="tech in ENERGY_TECHNOLOGIES" :key="tech">
                        - {{ TECHNOLOGIES[tech].name }}:
                        {{
                          (
                            statistics.registry_device_power_by_technology[
                              tech
                            ] / 1000
                          ).toFixed(2)
                        }}
                        MW
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-8">
                  <div class="row g-3">
                    <div
                      v-for="(
                        power, country
                      ) in statistics.power_summary_by_country"
                      :key="country"
                      class="col-sm-6 col-lg-4 mb-2"
                    >
                      <h6 class="mb-1 border-bottom pb-1">{{ country }}</h6>
                      <p class="card-text mb-1 small">
                        <strong>Total:</strong>
                        {{
                          (
                            sumTechnologyTotals(power.power_by_technology) /
                            1000
                          ).toFixed(2)
                        }}
                        MW
                      </p>
                      <div
                        class="ms-2 small text-muted"
                        style="font-size: 0.75rem"
                      >
                        <template
                          v-for="tech in ENERGY_TECHNOLOGIES"
                          :key="tech"
                        >
                          <div v-if="power.power_by_technology[tech] > 0">
                            {{ TECHNOLOGIES[tech].name }}:
                            {{
                              (power.power_by_technology[tech] / 1000).toFixed(
                                2
                              )
                            }}
                            MW
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 2: Unfiltered Statistics -->
    <div class="mb-4">
      <h2 class="mb-3">Fee Statistics</h2>
      <p class="text-muted mb-4">This section is not affected by filters</p>

      <div class="row g-3">
        <!-- Fee Statistics -->
        <div class="col-12 col-md-6 col-xl-4">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">Fee Statistics</h5>
              <p class="text-muted small mb-2">
                Annual production for fee calculation (company-by-company sum).
                Not affected by filters.
              </p>
              <h6>Overall Statistics</h6>
              <p class="card-text mb-1">
                Total projected production:
                {{ (feeStatistics.total_stats.total_power / 1000).toFixed(2) }}
                GWh/year
              </p>
              <p class="card-text mb-2">
                Weighted Average Fee:
                {{ feeStatistics.total_stats.weighted_avg_fee }}%
              </p>
              <div
                v-for="(stats, country) in feeStatistics.country_stats"
                :key="country"
              >
                <h6 class="mb-1">{{ country }}</h6>
                <p class="card-text mb-1">
                  Total Power: {{ (stats.total_power / 1000).toFixed(2) }} GWh
                </p>
                <p class="card-text mb-2">
                  Weighted Average Fee: {{ stats.weighted_avg_fee }}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AdminAllocationChart from "@/admin/AdminAllocationChart.vue";
import Chart from "@/chart-setup";
import CompanyMultiSelectComponent from "@/components/common/CompanyMultiSelectComponent.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import { getChartColor, PALETTE } from "@/composables/chartPalette";
import { goSolidApi } from "@/composables/useGoSolidApi";
import {
  MONTH_NAMES,
  PORTFOLIO_TYPES,
  SUPPORTED_COUNTRIES_WITH_LABELS,
  TECHNOLOGY_TYPES,
} from "@/constants";
import { PowerProjection } from "@/types";
import {
  ENERGY_TECHNOLOGIES,
  TECHNOLOGIES,
  type EnergyTechnology,
} from "@/types/technology";
import {
  computed,
  inject,
  nextTick,
  onMounted,
  ref,
  useTemplateRef,
} from "vue";

interface SupportCounts {
  supported: number;
  unsupported: number;
}

interface AgeBreakdown {
  [age: string]: SupportCounts;
}

interface PriceBreakdown {
  [price: string]: AgeBreakdown;
}

interface MonthBreakdown {
  [month: string]: PriceBreakdown;
}

interface TechnologyBreakdown {
  [techCode: string]: MonthBreakdown;
}

interface CountryBreakdown {
  [country: string]: TechnologyBreakdown;
}

interface TechCodeColor {
  supported: string;
  unsupported: string;
}

interface CountryLegendItem {
  label: string;
  value: number;
  color: string;
  percentage: string;
}

type TechnologyTotals = Record<EnergyTechnology, number>;

type ProjectedPowerTotals = TechnologyTotals & { total: number };

interface CountryPower {
  power_by_technology: TechnologyTotals;
  projected_power_by_technology: TechnologyTotals;
}

interface Country {
  [country: string]: CountryPower;
}

interface Statistics {
  account_count: number;
  company_count: number;
  companies_with_generators_count: number;
  companies_with_registry_devices_count: number;
  companies_with_certificates_count: number;
  companies_with_signed_agreement: number;
  total_generators: number;
  total_registry_devices: number;
  total_generator_power: number;
  registered_generator_power: number;
  total_registry_device_power: number;
  registry_device_power_by_technology: TechnologyTotals;
  registry_projections_by_technology: Record<EnergyTechnology, PowerProjection>;
  projected_power_totals: ProjectedPowerTotals;
  power_summary_by_country: Country;
}

interface FeeStatistics {
  country_stats: {
    [country: string]: {
      total_power: number;
      weighted_avg_fee: number;
    };
  };
  total_stats: {
    total_power: number;
    weighted_avg_fee: number;
  };
}

const globalErrorHandler = inject("globalErrorHandler") as (error: any) => void;

const zeroByTechnology = (): TechnologyTotals =>
  Object.fromEntries(
    ENERGY_TECHNOLOGIES.map((tech) => [tech, 0])
  ) as TechnologyTotals;

const emptyProjectionsByTechnology = (): Record<
  EnergyTechnology,
  PowerProjection
> =>
  Object.fromEntries(ENERGY_TECHNOLOGIES.map((tech) => [tech, {}])) as Record<
    EnergyTechnology,
    PowerProjection
  >;

const sumTechnologyTotals = (totals: TechnologyTotals): number =>
  ENERGY_TECHNOLOGIES.reduce((acc, tech) => acc + (totals[tech] || 0), 0);

const statistics = ref<Statistics>({
  account_count: 0,
  company_count: 0,
  companies_with_generators_count: 0,
  companies_with_registry_devices_count: 0,
  companies_with_certificates_count: 0,
  companies_with_signed_agreement: 0,
  total_generators: 0,
  total_registry_devices: 0,
  total_generator_power: 0,
  registered_generator_power: 0,
  total_registry_device_power: 0,
  registry_device_power_by_technology: zeroByTechnology(),
  registry_projections_by_technology: emptyProjectionsByTechnology(),
  projected_power_totals: { ...zeroByTechnology(), total: 0 },
  power_summary_by_country: {},
});

const feeStatistics = ref<FeeStatistics>({
  country_stats: {},
  total_stats: {
    total_power: 0,
    weighted_avg_fee: 0,
  },
});

const isLoading = ref(false);
const projectionsChartInstance = ref<Chart<"bar", number[], string> | null>(
  null
);
const projectionChartRef =
  useTemplateRef<HTMLCanvasElement>("projectionChartRef");
const chartRef = useTemplateRef<HTMLCanvasElement>("chartRef");
const chartInstance = ref<Chart<"bar", number[], string> | null>(null);
const countryChartRef = useTemplateRef<HTMLCanvasElement>("countryChartRef");
const countryChartInstance = ref<Chart<"doughnut", number[], string> | null>(
  null
);
const countryLegendItems = ref<CountryLegendItem[]>([]);
const certificateBreakdown = ref<CountryBreakdown>({});

const filters = ref({
  country_codes: [] as string[],
  portfolio_types: PORTFOLIO_TYPES.map((t) => t.portfolio_type),
  technology_prefixes: TECHNOLOGY_TYPES.map((t) => t.tech_code_prefix),
  expected_price: null as number | null | "",
  company_ids: [] as number[],
});

const portfolioTypeOptions = PORTFOLIO_TYPES;

const totalGOs = computed(() => {
  let total = 0;
  Object.values(certificateBreakdown.value).forEach((countryData) => {
    Object.values(countryData).forEach((techData: MonthBreakdown) => {
      Object.values(techData).forEach((monthData: PriceBreakdown) => {
        Object.values(monthData).forEach((priceData: AgeBreakdown) => {
          Object.values(priceData).forEach((ageCounts: SupportCounts) => {
            total += (ageCounts.supported ?? 0) + (ageCounts.unsupported ?? 0);
          });
        });
      });
    });
  });
  return total;
});

const supportedGOs = computed(() => {
  let total = 0;
  Object.values(certificateBreakdown.value).forEach((countryData) => {
    Object.values(countryData).forEach((techData: MonthBreakdown) => {
      Object.values(techData).forEach((monthData: PriceBreakdown) => {
        Object.values(monthData).forEach((priceData: AgeBreakdown) => {
          Object.values(priceData).forEach((ageCounts: SupportCounts) => {
            total += ageCounts.supported ?? 0;
          });
        });
      });
    });
  });
  return total;
});

const unsupportedGOs = computed(() => {
  let total = 0;
  Object.values(certificateBreakdown.value).forEach((countryData) => {
    Object.values(countryData).forEach((techData: MonthBreakdown) => {
      Object.values(techData).forEach((monthData: PriceBreakdown) => {
        Object.values(monthData).forEach((priceData: AgeBreakdown) => {
          Object.values(priceData).forEach((ageCounts: SupportCounts) => {
            total += ageCounts.unsupported ?? 0;
          });
        });
      });
    });
  });
  return total;
});

const buildQueryString = (): string => {
  const params = new URLSearchParams();

  filters.value.country_codes.forEach((code: string) =>
    params.append("country_codes", code)
  );

  if (
    filters.value.expected_price !== null &&
    filters.value.expected_price !== ""
  ) {
    params.append("expected_price", String(filters.value.expected_price));
  }

  filters.value.portfolio_types.forEach((type: string) => {
    params.append("portfolio_types", type);
  });

  filters.value.technology_prefixes.forEach((tech: string) =>
    params.append("technology_prefixes", tech)
  );

  filters.value.company_ids.forEach((id: number) =>
    params.append("company_ids", id.toString())
  );

  return params.toString();
};

const fetchData = async () => {
  try {
    isLoading.value = true;
    const response = await goSolidApi.get(
      `/api/admin/dashboard?${buildQueryString()}`
    );

    statistics.value = response.data.statistics;
    certificateBreakdown.value = response.data.certificate_breakdown;

    nextTick(() => {
      renderProjectionChart();
      renderChart();
      renderCountryChart();
    });
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

const fetchFeeStats = async () => {
  try {
    const response = await goSolidApi.get("/api/money/admin/fee-statistics");
    feeStatistics.value = response.data;
  } catch (error) {
    globalErrorHandler(error);
  }
};

const renderProjectionChart = () => {
  if (projectionChartRef.value) {
    const ctx = projectionChartRef.value.getContext("2d");
    if (ctx) {
      const monthNames = MONTH_NAMES;

      const datasets = ENERGY_TECHNOLOGIES.map((tech) => {
        const color = getChartColor({
          field: "technology",
          value: TECHNOLOGIES[tech].name,
        });
        return {
          label: TECHNOLOGIES[tech].name,
          data: monthNames.map(
            (_, index) =>
              statistics.value.registry_projections_by_technology[tech]?.[
                index + 1
              ] ?? 0
          ),
          backgroundColor: color,
          borderColor: color,
          borderWidth: 1,
        };
      });

      if (projectionsChartInstance.value != null) {
        projectionsChartInstance.value.destroy();
      }
      projectionsChartInstance.value = new Chart(ctx, {
        type: "bar",
        data: {
          labels: monthNames,
          datasets,
        },
        options: {
          scales: {
            x: {
              stacked: true,
            },
            y: {
              stacked: true,
              beginAtZero: true,
              title: {
                display: true,
                text: "Estimated Production (MWh)",
              },
            },
          },
          responsive: true,
          maintainAspectRatio: false,
        },
      });
    }
  }
};

const getTechCodeName = (techCode: string | number): string => {
  const tech = TECHNOLOGY_TYPES.find((t) => t.tech_code_prefix === techCode);
  return tech?.name ?? String(techCode);
};

const techCodeColors: Record<string, TechCodeColor> = {
  T01: {
    supported: getChartColor({ field: "technology", value: "Solar" }),
    unsupported: getChartColor({ field: "technology", value: "Solar" }) + "80",
  },
  T02: {
    supported: getChartColor({ field: "technology", value: "Wind" }),
    unsupported: getChartColor({ field: "technology", value: "Wind" }) + "80",
  },
  T03: {
    supported: getChartColor({ field: "technology", value: "Hydro" }),
    unsupported: getChartColor({ field: "technology", value: "Hydro" }) + "80",
  },
  T05: {
    supported: getChartColor({ field: "technology", value: "Biomass" }),
    unsupported:
      getChartColor({ field: "technology", value: "Biomass" }) + "80",
  },
  T06: {
    supported: getChartColor({ field: "technology", value: "Nuclear" }),
    unsupported:
      getChartColor({ field: "technology", value: "Nuclear" }) + "80",
  },
};

const renderChart = () => {
  if (chartRef.value) {
    const ctx = chartRef.value.getContext("2d");
    if (ctx) {
      const countries = Object.keys(certificateBreakdown.value);
      const months = new Set<string>();
      const techCodes = new Set<string>();

      countries.forEach((country) => {
        Object.keys(certificateBreakdown.value[country]).forEach((techCode) => {
          techCodes.add(techCode);
          Object.keys(certificateBreakdown.value[country][techCode]).forEach(
            (month) => {
              months.add(month);
            }
          );
        });
      });

      const sortedMonths = Array.from(months).sort();

      const datasets = Array.from(techCodes).flatMap((techCode) => {
        const techName = getTechCodeName(techCode);

        const supportedData = sortedMonths.map((month) => {
          return (
            Object.values(certificateBreakdown.value) as TechnologyBreakdown[]
          ).reduce((countryTotal: number, countryData: TechnologyBreakdown) => {
            if (!countryData[techCode]?.[month]) return countryTotal;

            return (
              countryTotal +
              Object.values(countryData[techCode][month]).reduce(
                (priceTotal: number, priceData: AgeBreakdown) =>
                  priceTotal +
                  Object.values(priceData).reduce(
                    (ageTotal: number, counts: SupportCounts) =>
                      ageTotal + (counts.supported ?? 0),
                    0
                  ),
                0
              )
            );
          }, 0);
        });

        const unsupportedData = sortedMonths.map((month) => {
          return (
            Object.values(certificateBreakdown.value) as TechnologyBreakdown[]
          ).reduce((countryTotal: number, countryData: TechnologyBreakdown) => {
            if (!countryData[techCode]?.[month]) return countryTotal;

            return (
              countryTotal +
              Object.values(countryData[techCode][month]).reduce(
                (priceTotal: number, priceData: AgeBreakdown) =>
                  priceTotal +
                  Object.values(priceData).reduce(
                    (ageTotal: number, counts: SupportCounts) =>
                      ageTotal + (counts.unsupported ?? 0),
                    0
                  ),
                0
              )
            );
          }, 0);
        });

        const { supported: supportedColor, unsupported: unsupportedColor } =
          techCodeColors[techCode] ?? {
            supported: "rgba(201, 203, 207, 1)",
            unsupported: "rgba(201, 203, 207, 0.5)",
          };

        return [
          {
            label: `${techName} (Supported)`,
            data: supportedData,
            backgroundColor: supportedColor,
            borderColor: supportedColor,
            borderWidth: 1,
          },
          {
            label: `${techName} (Unsupported)`,
            data: unsupportedData,
            backgroundColor: unsupportedColor,
            borderColor: unsupportedColor,
            borderWidth: 1,
          },
        ];
      });

      if (chartInstance.value != null) {
        chartInstance.value.destroy();
      }

      chartInstance.value = new Chart(ctx, {
        type: "bar",
        data: {
          labels: sortedMonths,
          datasets: datasets,
        },
        options: {
          scales: {
            x: {
              stacked: false,
            },
            y: {
              stacked: false,
              beginAtZero: true,
            },
          },
          responsive: true,
          maintainAspectRatio: false,
        },
      });
    }
  }
};

const renderCountryChart = () => {
  if (countryChartRef.value) {
    const ctx = countryChartRef.value.getContext("2d");
    if (ctx) {
      const countryTotals: Record<string, number> = {};

      (
        Object.entries(certificateBreakdown.value) as [
          string,
          TechnologyBreakdown,
        ][]
      ).forEach(([country, countryData]) => {
        let countryTotal = 0;
        Object.values(countryData).forEach((techData: MonthBreakdown) => {
          Object.values(techData).forEach((monthData: PriceBreakdown) => {
            Object.values(monthData).forEach((priceData: AgeBreakdown) => {
              Object.values(priceData).forEach((ageCounts: SupportCounts) => {
                countryTotal +=
                  (ageCounts.supported ?? 0) + (ageCounts.unsupported ?? 0);
              });
            });
          });
        });
        countryTotals[country] = countryTotal;
      });

      const countries = Object.keys(countryTotals);
      const totals = Object.values(countryTotals);

      if (countryChartInstance.value != null) {
        countryChartInstance.value.destroy();
        countryChartInstance.value = null;
      }

      if (countries.length === 0) {
        countryLegendItems.value = [];
        return;
      }

      const baseColors = PALETTE.categorical.slice(0, 10);

      const backgroundColors = countries.map(
        (_, index) => baseColors[index % baseColors.length]
      );

      const borderColors = backgroundColors;

      const totalValue = totals.reduce((sum, value) => sum + value, 0);

      countryLegendItems.value = countries.map((country, index) => {
        const value = totals[index] ?? 0;
        const percentage =
          totalValue > 0
            ? ((value / totalValue) * 100).toFixed(2).padStart(5, "0")
            : "00.00";

        return {
          label: country,
          value: value,
          color: backgroundColors[index] ?? "rgba(201, 203, 207, 0.8)",
          percentage: percentage,
        };
      });

      countryChartInstance.value = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: countries,
          datasets: [
            {
              data: totals,
              backgroundColor: backgroundColors,
              borderColor: borderColors,
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            legend: {
              display: false,
            },
          },
        },
      });
    }
  }
};

const onFilterChange = async () => {
  try {
    await fetchData();
  } catch (error) {
    globalErrorHandler(error);
  }
};

onMounted(async () => {
  try {
    const results = await Promise.allSettled([fetchData(), fetchFeeStats()]);

    results.forEach((result, index) => {
      if (result.status === "rejected") {
        const operation = ["dashboard data", "fee statistics"][index];
        console.error(`Failed to fetch ${operation}:`, result.reason);
        globalErrorHandler(result.reason);
      }
    });
  } catch (error) {
    globalErrorHandler(error);
  }
});
</script>

<style scoped lang="scss">
#AdminDashboard {
  flex-grow: 1;
  text-align: left;
  overflow-y: auto;
  max-width: 100%;
}

.portfolio-dropdown {
  max-height: 250px;
  overflow-y: auto;
}

.dropdown-item {
  padding: 0.25rem 1rem;
}

.dropdown-item .form-check {
  min-height: 1.5rem;
  padding-left: 1.5rem;
  margin-bottom: 0;
}

.dropdown-item:hover {
  background-color: var(--phoenix-gray-100);
}

/* Minimal custom styles - most layout uses Bootstrap utility classes */
.card {
  border-radius: 0.5rem;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.content-loading {
  opacity: 0.5;
  pointer-events: none;
}
</style>
